port module Frontend exposing (main)

import Backend.Code exposing (CodeId)
import Backend.File as File exposing (File)
import Backend.Settings.ClosedAfter as ClosedAfter
import Basics.Extra exposing (uncurry)
import Browser
import Codec exposing (Codec)
import Codec.Extra as Codec
import Dict
import Frontend.Api as Api
import Frontend.Checkout as Checkout exposing (Checkout)
import Frontend.Code exposing (Code, CodeId)
import Frontend.DeviceClass as DeviceClass exposing (DeviceClass)
import Frontend.Discount as Discount exposing (Discount)
import Frontend.Field as Field
import Frontend.FieldType as FieldType
import Frontend.FileUpload as FileUpload exposing (FileUploads)
import Frontend.FileUpload.Internal as FileUpload exposing (FileUploads)
import Frontend.Form as Form
import Frontend.Form.Internal as Form_ exposing (Form(..))
import Frontend.Form.Menu as Menu
import Frontend.Form.MobileMenu as MobileMenu
import Frontend.Form.ViewConfig.Standard as ViewConfig
import Frontend.Login as Login
import Frontend.Login.User as User exposing (User)
import Frontend.Markdown as Markdown
import Frontend.Order as Order exposing (Order)
import Frontend.OrderInput as OrderInput
import Frontend.Page as Page exposing (Page(..))
import Frontend.PersonInput as PersonInput
import Frontend.Settings exposing (Settings)
import Frontend.Ticket as Ticket exposing (Ticket, TicketLike)
import Frontend.UserAccountSettings exposing (UserAccountSettings)
import Html exposing (Html)
import Html.Attributes exposing (class, style)
import Http
import Http.Extra as Http
import Json.Decode as Decode exposing (Decoder)
import Json.Encode exposing (Value)
import List.Extra as List
import List.NonEmpty as NonEmpty exposing (NonEmpty)
import Locale exposing (Locale)
import Markdown.Block
import Markdown.Html as Markdown
import Markdown.Renderer as Markdown
import Material.Button as Button
import Material.CircularProgress as CircularProgress
import Material.Dialog as Dialog
import Material.Icon as Icon
import Material.List as List_
import Material.List.Item as ListItem
import Material.TextField as TextField
import Material.Theme as Theme
import Maybe.Extra as Maybe
import Result.Extra as Result
import Task
import Time
import Translated exposing (Translated)
import Url exposing (Url)


type alias Flags =
    { values : Maybe Value
    , pages : Maybe Value
    , idempotencyToken : String
    , locale : Maybe String
    , tickets : Maybe Value
    , discounts : Maybe Value
    , code : Maybe String
    , exhibitor : Maybe String
    , id : Maybe String
    , crashed : Maybe String
    , checkout : Maybe Value
    , loading : Maybe Bool
    , origin : String
    , width : Maybe Int
    , auth : Maybe Api.Auth
    }


main : Program Flags (Login.Model Flags Msg Model) (Login.Msg Msg)
main =
    Browser.element
        { init = init
        , subscriptions = subscriptions
        , update = update
        , view = view
        }


type Error
    = CustomError String
    | DecodeError Decode.Error
    | HttpError Http.Error
    | MarkdownError String


errorToString : Error -> String
errorToString error =
    case error of
        DecodeError decodeError ->
            "decode error: " ++ Decode.errorToString decodeError

        HttpError httpError ->
            "http error: " ++ Http.errorToString httpError

        MarkdownError markdownError ->
            "markdown error: " ++ markdownError

        CustomError customError ->
            customError


type Model
    = Crashed CrashedData
    | QuotaExceeded QuotaExceededData
    | Loaded LoadedData
    | Loading
    | Finished FinishedData
    | Paying PayingData
    | Closed ClosedData


type alias PayingData =
    { defaultLocale : Locale
    , locale : Locale
    , checkout : Checkout
    }


type alias CrashedData =
    { defaultLocale : Locale
    , locale : Locale
    , error : Error
    }


type alias QuotaExceededData =
    { defaultLocale : Locale
    , locale : Locale
    , code : String
    }


type alias LoadedData =
    { pages : List Page
    , form : Form.Values
    , idempotencyToken : String
    , defaultLocale : Locale
    , locale : Locale
    , code : Maybe Code
    , tickets : List Ticket
    , discounts : List Discount
    , width : Maybe Int
    , settings : Settings
    , confirmationText : Translated String
    , fileUploads : FileUploads
    , origin : Maybe Url
    , user : Maybe User
    , update : Bool
    , userAccountSettings : Maybe UserAccountSettings
    , maybeOrder : Maybe Order
    , mobileMenuOpen : Bool
    , flags : Flags
    , dialog : Maybe Dialog
    , auth : Maybe Api.Auth
    }


type Dialog
    = DialogOpen DialogData
    | DialogClosed DialogData


closeDialog dialog =
    case dialog of
        DialogOpen dialog_ ->
            DialogClosed dialog_

        DialogClosed _ ->
            dialog


isDialogOpen dialog =
    case dialog of
        DialogOpen _ ->
            True

        DialogClosed _ ->
            False


dialogData dialog =
    case dialog of
        DialogOpen data ->
            data

        DialogClosed data ->
            data


type alias DialogData =
    { content : List (Html ())
    , onConfirm : Form.Values
    }


type alias FinishedData =
    { confirmationText : Translated String
    , defaultLocale : Locale
    , locale : Locale
    , idempotencyToken : String
    }


type alias ClosedData =
    { closedText : Translated String
    , defaultLocale : Locale
    , locale : Locale
    }


type Msg
    = PagesChanged (Result Decode.Error (List Page))
    | FormChanged (Form.Msg Form.Values Order)
    | LocaleChanged (Result Decode.Error Locale)
    | Submitted (Result Http.Error Api.Submission)
    | TicketsChanged (Result Decode.Error (List Ticket))
    | DiscountsChanged (Result Decode.Error (List Discount))
    | WidthChanged (Maybe Int)
    | Initialized ( Model, Cmd Msg )
    | Uploaded (NonEmpty String) File (Result Http.Error String)
    | LogoutReceived (Result Http.Error ())
    | DialogDismissed
    | DialogConfirmed Form.Values
    | NoOp



{-
   submissionCodec : Codec Submission
   submissionCodec =
       Codec.custom
           (\paymentRequired submissionException submissionOk value ->
               case value of
                   PaymentRequired data ->
                       paymentRequired data

                   SubmissionException data ->
                       submissionException data

                   SubmissionOk data ->
                       submissionOk data
           )
           |> Codec.variant1 "PaymentRequired"
               PaymentRequired
               paymentRequiredDataCodec
           |> Codec.variant1 "SubmissionException"
               SubmissionException
               submissionExceptionDataCodec
           |> Codec.variant1 "SubmissionOk"
               SubmissionOk
               submissionOkDataCodec
           |> Codec.buildCustom
-}


port submit : Value -> Cmd msg


port inspect : Value -> Cmd msg


port navigated : () -> Cmd msg


port mountCheckout : Value -> Cmd msg


init : Flags -> ( Login.Model Flags Msg Model, Cmd (Login.Msg Msg) )
init flags =
    Login.init
        { referringUrl = flags.origin
        , locale = Maybe.map Locale.fromString flags.locale
        , auth = flags.auth
        , maybeExhibitor = flags.exhibitor
        }
        init_
        flags


init_ : Flags -> Maybe ( User, Maybe Api.Auth ) -> ( Model, Cmd Msg )
init_ flags loginResponse =
    let
        fetchCode maybeOrder maybeExhibitor =
            Maybe.unwrap (Task.succeed (Ok Nothing))
                (\code ->
                    Task.map (Result.map Just)
                        (Api.redeemCode flags.auth
                            { code = code
                            }
                            |> Task.mapError HttpError
                        )
                )
                (case maybeOrder of
                    Just order ->
                        Order.code order

                    Nothing ->
                        case maybeExhibitor of
                            Just exhibitor ->
                                Just exhibitor

                            Nothing ->
                                flags.code
                )

        fetchOrder pages user_ =
            Task.mapError HttpError
                (Api.fetchRegistration flags.auth user_)

        fetchPages =
            Maybe.unwrap
                (Task.mapError HttpError (Api.fetchPages flags.auth))
                (\pages ->
                    Task.mapError DecodeError
                        (Result.toTask
                            (Decode.decodeValue (Decode.list Page.decoder)
                                pages
                            )
                        )
                )
                flags.pages

        fetchTickets =
            Maybe.unwrap
                (Task.mapError HttpError
                    (Api.fetchTickets flags.auth
                        { code = flags.code
                        }
                    )
                )
                (\tickets ->
                    Task.mapError DecodeError
                        (Result.toTask
                            (Decode.decodeValue (Decode.list Ticket.decoder)
                                tickets
                            )
                        )
                )
                flags.tickets

        fetchDiscounts =
            Maybe.unwrap (Task.mapError HttpError (Api.fetchDiscounts flags.auth))
                (\discounts ->
                    Task.mapError DecodeError
                        (Result.toTask
                            (Decode.decodeValue (Decode.list Discount.decoder)
                                discounts
                            )
                        )
                )
                flags.discounts

        fetchForm maybeOrder pages =
            Maybe.unwrap
                (case maybeOrder of
                    Just order ->
                        Task.succeed
                            (Just
                                (Form.fromOrderInput (OrderInput.fromOrder (Page.globalTicketFields pages) order))
                            )

                    Nothing ->
                        Task.succeed Nothing
                )
                (\values ->
                    Task.mapError DecodeError
                        (Task.map Just
                            (Result.toTask
                                (Decode.decodeValue
                                    (Decode.map (Maybe.withDefault Form.initialValues)
                                        (Decode.nullable Form.valuesDecoder)
                                    )
                                    values
                                )
                            )
                        )
                )
                flags.values
    in
    ( Loading
    , Task.map2 Tuple.pair
        fetchPages
        (Api.fetchUserAccountSettings flags.auth
            |> Task.mapError HttpError
        )
        |> Task.andThen
            (\( pages, userAccountSettings ) ->
                (case
                    ( userAccountSettings
                    , Maybe.map Tuple.first loginResponse
                    )
                 of
                    ( Just _, Just user_ ) ->
                        fetchOrder pages user_

                    _ ->
                        Task.succeed Nothing
                )
                    |> Task.map
                        (\maybeOrder ->
                            { userAccountSettings = userAccountSettings
                            , pages = pages
                            , maybeOrder = maybeOrder
                            }
                        )
            )
        |> Task.andThen
            (\{ userAccountSettings, pages, maybeOrder } ->
                Task.map3
                    (\now settings codeResult ->
                        { now = now
                        , settings = settings
                        , codeResult = codeResult
                        }
                    )
                    Time.now
                    (Api.fetchSettings flags.auth
                        |> Task.mapError HttpError
                    )
                    (fetchCode maybeOrder flags.exhibitor)
                    |> Task.map
                        (\{ now, settings, codeResult } ->
                            let
                                defaultLocale =
                                    NonEmpty.head settings.locales

                                locale =
                                    case maybeOrder of
                                        Just order ->
                                            Order.locale order

                                        Nothing ->
                                            Maybe.unwrap defaultLocale Locale.fromString flags.locale
                            in
                            [ case flags.loading of
                                Just True ->
                                    Just ( Loading, Cmd.none )

                                _ ->
                                    Nothing
                            , Maybe.map
                                (\customError ->
                                    ( Crashed
                                        { defaultLocale = defaultLocale
                                        , locale = locale
                                        , error = CustomError customError
                                        }
                                    , Cmd.none
                                    )
                                )
                                flags.crashed
                            , Maybe.map
                                (\checkoutValue ->
                                    case Decode.decodeValue (Codec.decoder Checkout.codec) checkoutValue of
                                        Ok checkout ->
                                            ( Paying
                                                { defaultLocale = defaultLocale
                                                , locale = locale
                                                , checkout = checkout
                                                }
                                            , mountCheckout (Codec.encoder Checkout.codec checkout)
                                            )

                                        Err decodeError ->
                                            ( Crashed
                                                { defaultLocale = defaultLocale
                                                , locale = locale
                                                , error = DecodeError decodeError
                                                }
                                            , Cmd.none
                                            )
                                )
                                flags.checkout
                            , Maybe.map
                                (\id ->
                                    ( Loading
                                    , Task.perform Initialized
                                        (Task.onError
                                            (\error ->
                                                Task.succeed
                                                    ( Crashed
                                                        { defaultLocale = defaultLocale
                                                        , locale = locale
                                                        , error = error
                                                        }
                                                    , Cmd.none
                                                    )
                                            )
                                            (Task.mapError HttpError
                                                (Task.map
                                                    (\_ ->
                                                        ( Finished
                                                            { confirmationText = settings.confirmationText
                                                            , defaultLocale = defaultLocale
                                                            , locale = locale
                                                            , idempotencyToken = id
                                                            }
                                                        , Cmd.none
                                                        )
                                                    )
                                                    (Api.fetchStatus flags.auth
                                                        { id = id
                                                        }
                                                    )
                                                )
                                            )
                                        )
                                    )
                                )
                                flags.id
                            , Maybe.andThen
                                (\closedAfter ->
                                    if
                                        ClosedAfter.isClosed
                                            (case codeResult of
                                                Err _ ->
                                                    Just ()

                                                Ok Nothing ->
                                                    Nothing

                                                Ok (Just _) ->
                                                    Just ()
                                            )
                                            now
                                            closedAfter
                                    then
                                        Just
                                            ( Closed
                                                { defaultLocale = defaultLocale
                                                , locale = locale
                                                , closedText = settings.closedText
                                                }
                                            , Cmd.none
                                            )

                                    else
                                        Nothing
                                )
                                settings.closedAfter
                            ]
                                |> Maybe.values
                                |> List.head
                                |> Maybe.withDefault
                                    ( Loading
                                    , Task.map3
                                        (\tickets discounts form ->
                                            case codeResult of
                                                Err (Api.QuotaExceeded code) ->
                                                    ( QuotaExceeded
                                                        { defaultLocale = defaultLocale
                                                        , locale = locale
                                                        , code = code
                                                        }
                                                    , Cmd.none
                                                    )

                                                Ok code ->
                                                    let
                                                        loadedData =
                                                            { pages = pages
                                                            , form = Maybe.withDefault Form.initialValues form
                                                            , idempotencyToken = flags.idempotencyToken
                                                            , defaultLocale = defaultLocale
                                                            , locale = locale
                                                            , code = code
                                                            , tickets = tickets
                                                            , discounts = discounts
                                                            , width = flags.width
                                                            , settings = settings
                                                            , confirmationText =
                                                                case maybeOrder of
                                                                    Nothing ->
                                                                        settings.confirmationText

                                                                    Just _ ->
                                                                        Maybe.unwrap settings.confirmationText .updateText userAccountSettings
                                                            , fileUploads = FileUpload.empty
                                                            , origin = Url.fromString flags.origin
                                                            , user = Maybe.map Tuple.first loginResponse
                                                            , update = Maybe.isJust form
                                                            , userAccountSettings = userAccountSettings
                                                            , maybeOrder = maybeOrder
                                                            , mobileMenuOpen = False
                                                            , flags = flags
                                                            , dialog = Nothing
                                                            , auth =
                                                                Maybe.andThen Tuple.second loginResponse
                                                                    |> Maybe.orElse flags.auth
                                                            }

                                                        navigateToSummary values =
                                                            let
                                                                hasSummaryField page =
                                                                    List.any
                                                                        (\field ->
                                                                            case field of
                                                                                Field.Field _ FieldType.Summary ->
                                                                                    True

                                                                                _ ->
                                                                                    False
                                                                        )
                                                                        (Page.flatFields page)

                                                                summaryPageCursor =
                                                                    List.head
                                                                        (List.filterMap
                                                                            (\{ cursors, page } ->
                                                                                if hasSummaryField page then
                                                                                    Just cursors.currentCursor

                                                                                else
                                                                                    Nothing
                                                                            )
                                                                            cursorizedPages
                                                                        )

                                                                cursorizedPages =
                                                                    Page.cursorize (Page.isMultiplePersons pages)
                                                                        (Form_.initialArgs
                                                                            (formConfig loadedData)
                                                                            values
                                                                            (ViewConfig.standard ViewConfig.ticketSelection)
                                                                            (\_ -> [])
                                                                        )
                                                                        pages
                                                            in
                                                            case summaryPageCursor of
                                                                Just cursor ->
                                                                    { values
                                                                        | cursor = cursor
                                                                    }

                                                                Nothing ->
                                                                    values
                                                    in
                                                    ( Loaded
                                                        { loadedData
                                                            | form =
                                                                (if Maybe.isJust maybeOrder then
                                                                    navigateToSummary

                                                                 else
                                                                    identity
                                                                )
                                                                    loadedData.form
                                                        }
                                                    , inspect (Form.encodeValues loadedData.form)
                                                    )
                                        )
                                        fetchTickets
                                        fetchDiscounts
                                        (fetchForm maybeOrder pages)
                                        |> Task.onError
                                            (\error ->
                                                Task.succeed
                                                    ( Crashed
                                                        { defaultLocale = defaultLocale
                                                        , locale = locale
                                                        , error = error
                                                        }
                                                    , Cmd.none
                                                    )
                                            )
                                        |> Task.perform Initialized
                                    )
                        )
            )
        |> Task.onError
            (\error ->
                Task.succeed
                    ( Crashed
                        { defaultLocale = Locale.Known Locale.De
                        , locale = Locale.Known Locale.De
                        , error = error
                        }
                    , Cmd.none
                    )
            )
        |> Task.perform Initialized
    )


port setPages : (Value -> msg) -> Sub msg


port setLocale : (String -> msg) -> Sub msg


port setTickets : (Value -> msg) -> Sub msg


port setDiscounts : (Value -> msg) -> Sub msg


port setWidth : (Maybe Int -> msg) -> Sub msg


subscriptions : Login.Model Flags Msg Model -> Sub (Login.Msg Msg)
subscriptions =
    Login.subscriptions subscriptions_


subscriptions_ : Model -> Sub Msg
subscriptions_ _ =
    Sub.batch
        [ setPages (PagesChanged << Decode.decodeValue (Decode.list Page.decoder))
        , setLocale (LocaleChanged << Ok << Locale.fromString)
        , setTickets (TicketsChanged << Decode.decodeValue (Decode.list Ticket.decoder))
        , setDiscounts (DiscountsChanged << Decode.decodeValue (Decode.list Discount.decoder))
        , setWidth WidthChanged
        ]


update :
    Login.Msg Msg
    -> Login.Model Flags Msg Model
    -> ( Login.Model Flags Msg Model, Cmd (Login.Msg Msg) )
update msg model =
    case ( msg, model ) of
        ( Login.Msg (LogoutReceived (Ok ())), Login.Model _ _ (Login.Done (Loaded loadedData)) ) ->
            init loadedData.flags

        ( Login.Msg (LogoutReceived (Err _)), Login.Model _ _ (Login.Done (Loaded loadedData)) ) ->
            -- TODO
            ( model, Cmd.none )

        _ ->
            Login.update update_ msg model


update_ : Msg -> Model -> ( Model, Cmd Msg )
update_ msg model =
    let
        pass =
            ( model, Cmd.none )
    in
    case ( msg, model ) of
        ( Initialized cont, Loading ) ->
            cont

        ( Initialized _, _ ) ->
            pass

        ( _, Loading ) ->
            pass

        ( _, Crashed _ ) ->
            pass

        ( _, QuotaExceeded _ ) ->
            pass

        ( FormChanged (Form.Submitted order), Loaded loadedData ) ->
            let
                orderInput =
                    OrderInput.fromOrder (Page.globalTicketFields loadedData.pages)
                        order

                discountPrice =
                    Maybe.unwrap 0 (.price << Tuple.second) <|
                        Discount.applys loadedData
                            orderInput
                            loadedData.discounts

                frontendPrice =
                    OrderInput.totalPrice loadedData orderInput
                        - discountPrice
            in
            ( Loaded loadedData
            , Cmd.batch
                [ submit (Order.encode order)
                , Task.attempt Submitted
                    (case loadedData.update of
                        True ->
                            Api.updateRegistration loadedData.auth order frontendPrice

                        False ->
                            Api.createRegistration loadedData.auth order loadedData.idempotencyToken frontendPrice loadedData.origin
                    )
                ]
            )

        ( FormChanged (Form.Changed newForm), Loaded loadedData ) ->
            let
                pageChanged =
                    newForm.cursor /= loadedData.form.cursor
            in
            ( Loaded
                { loadedData
                    | form = newForm
                }
            , Cmd.batch
                [ inspect (Form.encodeValues newForm)
                , if pageChanged then
                    navigated ()

                  else
                    Cmd.none
                ]
            )

        ( FormChanged (Form.Confirm currentPerson newForm), Loaded loadedData ) ->
            let
                values =
                    loadedData.form

                context =
                    formConfig loadedData

                form =
                    Page.toForm loadedData.pages
            in
            ( Loaded
                { loadedData
                    | dialog =
                        Just
                            (DialogOpen
                                { content = Form.toConfirm currentPerson context values form
                                , onConfirm = newForm
                                }
                            )
                }
            , Cmd.none
            )

        ( DialogConfirmed newForm, Loaded loadedData ) ->
            let
                pageChanged =
                    newForm.cursor /= loadedData.form.cursor
            in
            ( Loaded
                { loadedData
                    | form = newForm
                    , dialog = Maybe.map closeDialog loadedData.dialog
                }
            , Cmd.batch
                [ inspect (Form.encodeValues newForm)
                , if pageChanged then
                    navigated ()

                  else
                    Cmd.none
                ]
            )

        ( FormChanged (Form.UploadRequested maxSize key file), Loaded loadedData ) ->
            let
                ( newFileUploads, newForm ) =
                    FileUpload.insertPending maxSize
                        key
                        file
                        loadedData.fileUploads
                        loadedData.form
            in
            ( Loaded
                { loadedData
                    | fileUploads = newFileUploads
                    , form = newForm
                }
            , Cmd.batch
                [ inspect (Form.encodeValues newForm)
                , if file.size <= maxSize.maxSize then
                    Task.attempt (Uploaded key file) (Api.upload loadedData.auth file)

                  else
                    Cmd.none
                ]
            )

        ( FormChanged (Form.MobileMenuOpened mobileMenuOpen), Loaded loadedData ) ->
            ( Loaded { loadedData | mobileMenuOpen = mobileMenuOpen }, Cmd.none )

        ( FormChanged Form.LogoutClicked, Loaded loadedData ) ->
            ( Loaded loadedData, Task.attempt LogoutReceived (Api.logout loadedData.auth) )

        ( LogoutReceived _, _ ) ->
            -- cf. `update`
            ( model, Cmd.none )

        ( Uploaded key file (Ok fileName), Loaded loadedData ) ->
            let
                ( newFileUploads, newForm ) =
                    FileUpload.insertUploaded key
                        file
                        fileName
                        loadedData.fileUploads
                        loadedData.form
            in
            ( Loaded
                { loadedData
                    | fileUploads = newFileUploads
                    , form = newForm
                }
            , inspect (Form.encodeValues newForm)
            )

        ( Uploaded key file (Err error), Loaded loadedData ) ->
            let
                ( newFileUploads, newForm ) =
                    FileUpload.insertFailed key
                        file
                        error
                        loadedData.fileUploads
                        loadedData.form
            in
            ( Loaded
                { loadedData
                    | fileUploads = newFileUploads
                    , form = newForm
                }
            , inspect (Form.encodeValues newForm)
            )

        ( Uploaded _ _ _, _ ) ->
            pass

        ( PagesChanged (Err decodeError), Loaded loadedData ) ->
            ( Crashed
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , error = DecodeError decodeError
                }
            , Cmd.none
            )

        ( PagesChanged (Ok newPages), Loaded loadedData ) ->
            ( Loaded
                { loadedData
                    | pages = newPages
                }
            , Cmd.none
            )

        ( LocaleChanged (Err decodeError), Loaded loadedData ) ->
            ( Crashed
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , error = DecodeError decodeError
                }
            , Cmd.none
            )

        ( LocaleChanged (Ok newLocale), Loaded loadedData ) ->
            ( Loaded
                { loadedData
                    | locale = newLocale
                }
            , Cmd.none
            )

        ( Submitted (Err _), Loaded loadedData ) ->
            ( Crashed
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , error = CustomError "internal error"
                }
            , Cmd.none
            )

        ( Submitted (Err _), _ ) ->
            ( model, Cmd.none )

        ( Submitted (Ok (Api.SubmissionOk _)), Loaded loadedData ) ->
            ( Finished
                { confirmationText = loadedData.confirmationText
                , locale = loadedData.locale
                , defaultLocale = loadedData.defaultLocale
                , idempotencyToken = loadedData.idempotencyToken
                }
            , Cmd.none
            )

        ( Submitted (Ok (Api.PaymentRequired checkout)), Loaded loadedData ) ->
            ( Paying
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , checkout = checkout
                }
            , mountCheckout (Codec.encoder Checkout.codec checkout)
            )

        ( Submitted (Ok (Api.SubmissionException { error })), Loaded loadedData ) ->
            ( Crashed
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , error = CustomError error
                }
            , Cmd.none
            )

        ( Submitted (Ok (Api.SubmissionException _)), _ ) ->
            pass

        ( TicketsChanged (Err decodeError), Loaded loadedData ) ->
            ( Crashed
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , error = DecodeError decodeError
                }
            , Cmd.none
            )

        ( TicketsChanged (Ok newTickets), Loaded loadedData ) ->
            ( Loaded
                { loadedData
                    | tickets = newTickets
                }
            , Cmd.none
            )

        ( DiscountsChanged (Err decodeError), Loaded loadedData ) ->
            ( Crashed
                { defaultLocale = loadedData.defaultLocale
                , locale = loadedData.locale
                , error = DecodeError decodeError
                }
            , Cmd.none
            )

        ( DiscountsChanged (Ok newDiscounts), Loaded loadedData ) ->
            ( Loaded
                { loadedData
                    | discounts = newDiscounts
                }
            , Cmd.none
            )

        ( WidthChanged newWidth, Loaded loadedData ) ->
            ( Loaded
                { loadedData
                    | width = newWidth
                }
            , Cmd.none
            )

        ( DialogDismissed, Loaded loadedData ) ->
            ( Loaded { loadedData | dialog = Nothing }
            , Cmd.none
            )

        ( _, Finished _ ) ->
            pass

        ( _, Closed _ ) ->
            pass

        ( _, Paying _ ) ->
            pass

        ( NoOp, _ ) ->
            pass


view : Login.Model Flags Msg Model -> Html (Login.Msg Msg)
view =
    Login.view view_


view_ : Model -> Html Msg
view_ model =
    case model of
        Crashed data ->
            viewCrashed data

        QuotaExceeded data ->
            viewQuotaExceeded data

        Loaded data ->
            viewLoaded data

        Loading ->
            viewLoading

        Finished data ->
            viewFinished data

        Paying data ->
            viewPaying data

        Closed data ->
            viewClosed data


viewCrashed : CrashedData -> Html Msg
viewCrashed data =
    Html.div [ class "error-view" ]
        [ Result.unpack
            (\error -> Html.pre [] [ Html.text error ])
            (Html.div [])
            (Markdown.view
                (Translated.extractDef data
                    ""
                    (Translated.fromLocale
                        (\locale ->
                            case locale of
                                Locale.De ->
                                    """
# Technischer Fehler

Leider ist ein unerwarteter Fehler aufgetreten.
"""

                                Locale.En ->
                                    """
# Technical error

Unfortunately, we have encountered an unexpected error.
"""

                                Locale.Es ->
                                    """
# Error técnico

Desafortunadamente, se ha producido un error inesperado."
"""

                                Locale.Fr ->
                                    """
Erreur technique

Une erreur inattendue est malheureusement survenue.
"""

                                Locale.Ru ->
                                    """
Сбой в работе оборудования

К сожалению, мы столкнулись с непредвиденной ошибкой.
"""
                        )
                    )
                )
            )
        , Html.details []
            [ Html.summary []
                [ Html.text
                    (Translated.extractDef data
                        ""
                        (Translated.fromLocale
                            (\locale ->
                                case locale of
                                    Locale.De ->
                                        "Technische Details"

                                    Locale.En ->
                                        "Technical details"

                                    Locale.Es ->
                                        "Detalles técnicos"

                                    Locale.Fr ->
                                        "Détails techniques"

                                    Locale.Ru ->
                                        "Техническая информация, подробности"
                            )
                        )
                    )
                ]
            , Html.pre []
                [ Html.text (errorToString data.error)
                ]
            ]
        , Result.unpack
            (\error -> Html.pre [] [ Html.text error ])
            (Html.div [])
            (Markdown.view
                (Translated.extractDef data
                    ""
                    (Translated.fromLocale
                        (\locale ->
                            case locale of
                                Locale.De ->
                                    """
Bei Fragen zur Registrierung stehen wir Ihnen gern unter den folgenden Kontaktdaten zur Verfügung:

Telefon: [+49(0)511 898813-0](tel:+49511898813-0) - E-Mail: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.En ->
                                    """
Please do not hesitate to contact us in case of any further questions regarding your registration.

Phone: [+49(0)511 898813-0](tel:+49511898813-0) - E-Mail: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.Es ->
                                    """
Si tiene alguna pregunta sobre la inscripción, no dude en ponerse en contacto con nosotros utilizando los siguientes datos de contacto:

Teléfono: [+49(0)511 898813-0](tel:+49511898813-0) - Correo electrónico: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.Fr ->
                                    """
Si vous avez des questions concernant l'enregistrement, n'hésitez pas à nous contacter aux coordonnées suivantes:

Tél.: [+49(0)511 898813-0](tel:+49511898813-0) - E-mail: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.Ru ->
                                    """
Если у вас возникнут дополнительные вопросы относительно вашей регистрации, обращайтесь к нам:

Г-жа Сандрине Чех, телефон: [+49(0)511 898813-0](tel:+49511 898813-0) - Электронная почта: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""
                        )
                    )
                )
            )
        ]


viewQuotaExceeded : QuotaExceededData -> Html Msg
viewQuotaExceeded data =
    Html.div [ class "error-view" ]
        [ Result.unpack
            (\error -> Html.pre [] [ Html.text error ])
            (Html.div [])
            (Markdown.view
                (Translated.extractDef data
                    ""
                    (Translated.fromLocale
                        (\locale ->
                            case locale of
                                Locale.De ->
                                    """
# Kontingent erschöpft

Das Kontingent für den Code **"""
                                        ++ data.code
                                        ++ """** ist erschöpft. Haben Sie sich verschrieben oder sind Sie bereits angemeldet?
"""

                                Locale.En ->
                                    """
# Quota Exceeded

The quota for the code **"""
                                        ++ data.code
                                        ++ """** is exceeded. Did you spell the code incorrectly, or have you registered already?
"""

                                Locale.Es ->
                                    """
# Contingente agotado

Se ha superado la cuota para el código **"""
                                        ++ data.code
                                        ++ """** . ¿Ha escrito mal el código o ya se has registrado anteriormente?
"""

                                Locale.Fr ->
                                    """
Contingent dépassé

Le contingent pour le code **""" ++ data.code ++ """** est dépassé. Avez-vous fait une erreur ou êtes-vous déjà inscrit ?
"""

                                Locale.Ru ->
                                    """
Квота исчерпана

Квота для кода **"""
                                        ++ data.code
                                        ++ """** исчерпана. Вы допустили опечатку или уже зарегистрировались?
"""
                        )
                    )
                )
            )
        , Result.unpack
            (\error -> Html.pre [] [ Html.text error ])
            (Html.div [])
            (Markdown.view
                (Translated.extractDef data
                    ""
                    (Translated.fromLocale
                        (\locale ->
                            case locale of
                                Locale.De ->
                                    """
Bei Fragen zur Registrierung stehen wir Ihnen gern unter den folgenden Kontaktdaten zur Verfügung:

Telefon: [+49(0)511 898813-0](tel:+49511898813-0) - E-Mail: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.En ->
                                    """
Please do not hesitate to contact us in case of any further questions regarding your registration.

Phone: [+49(0)511 898813-0](tel:+49511898813-0) - E-Mail: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.Es ->
                                    """
Si tiene alguna pregunta sobre la inscripción, no dude en ponerse en contacto con nosotros utilizando los siguientes datos de contacto:

Teléfono: [+49(0)511 898813-0](tel:+49511898813-0) - Correo electrónico: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.Fr ->
                                    """
Si vous avez des questions concernant l'enregistrement, n'hésitez pas à nous contacter aux coordonnées suivantes:

Tél.: [+49(0)511 898813-0](tel:+49511898813-0) - E-mail: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""

                                Locale.Ru ->
                                    """
Если у вас возникнут дополнительные вопросы относительно вашей регистрации, обращайтесь к нам:

Г-жа Сандрине Чех, телефон: [+49(0)511 898813-0](tel:+49511 898813-0) - Электронная почта: [registrierungen@congresscheck.de](mailto:registrierungen@congresscheck.de)
"""
                        )
                    )
                )
            )
        ]


viewLoaded : LoadedData -> Html Msg
viewLoaded data =
    let
        values =
            data.form

        context =
            formConfig data

        form =
            Page.toForm data.pages
    in
    Html.div [ class "form-wrapper" ]
        [ debugBanner data
        , Html.map FormChanged
            (Form.toHtml context
                (if Maybe.isJust data.userAccountSettings then
                    case DeviceClass.fromWidth data.width of
                        DeviceClass.Desktop ->
                            Just
                                (Menu.menu
                                    { defaultLocale = data.defaultLocale
                                    , locale = data.locale
                                    }
                                    data.pages
                                    form
                                )

                        DeviceClass.Phone ->
                            Nothing

                 else
                    Nothing
                )
                (if Maybe.isJust data.userAccountSettings then
                    case DeviceClass.fromWidth data.width of
                        DeviceClass.Phone ->
                            Just
                                (MobileMenu.menu
                                    { defaultLocale = data.defaultLocale
                                    , locale = data.locale
                                    , mobileMenuOpen = data.mobileMenuOpen
                                    }
                                    data.pages
                                    form
                                )

                        DeviceClass.Desktop ->
                            Nothing

                 else
                    Nothing
                )
                []
                values
                form
            )
        , Dialog.confirmation
            (Dialog.config
                |> Dialog.setOpen (Maybe.unwrap False isDialogOpen data.dialog)
                |> Dialog.setOnClose DialogDismissed
            )
            { title = "Teilnehmenden entfernen"
            , content =
                [ Html.p []
                    [ Html.text "Durch das Entfernen des Teilnehmenden werden die folgenden assoziierten Buchungsdaten entfernt."
                    ]
                ]
                    ++ Maybe.unwrap []
                        (List.singleton
                            << Html.map (\_ -> NoOp)
                            << Html.div [ style "opacity" "0.5" ]
                            << .content
                            << dialogData
                        )
                        data.dialog
            , actions =
                [ Button.text
                    (Button.config |> Button.setOnClick DialogDismissed)
                    "Abbrechen"
                , Button.text
                    (Button.config
                        |> Button.setOnClick
                            (Maybe.unwrap DialogDismissed
                                (DialogConfirmed << .onConfirm << dialogData)
                                data.dialog
                            )
                        |> Button.setAttributes [ Dialog.defaultAction ]
                    )
                    "Enternen"
                ]
            }
        ]


formConfig : LoadedData -> Form.Context Ticket
formConfig data =
    { defaultLocale = data.defaultLocale
    , locale = data.locale
    , code = data.code
    , tickets = data.tickets
    , discounts = data.discounts
    , settings = data.settings
    , fileUploads = data.fileUploads
    , userAccountSettings = data.userAccountSettings
    , maybeOrder = data.maybeOrder
    }


debugBanner :
    { c
        | settings : Settings
        , defaultLocale : Locale
        , locale : Locale
    }
    -> Html msg
debugBanner config =
    if not config.settings.live then
        Html.div [ class "debug-banner" ]
            [ Html.text
                (Translated.extractDef config
                    ""
                    (Translated.fromLocale
                        (\locale ->
                            case locale of
                                Locale.De ->
                                    "Die Registrierung befindet sich in der Entwicklung. Zahlung ist NICHT live."

                                Locale.En ->
                                    "This registration is being developed. Payment is NOT live."

                                Locale.Es ->
                                    "La inscripción está en desarrollo. El pago NO está activo."

                                Locale.Fr ->
                                    "L'inscription est en cours de réalisation. Le paiement n'est PAS en direct."

                                Locale.Ru ->
                                    "Эта регистрация находится на стадии разработки. Функция оплаты НЕ в режиме «лайф»."
                        )
                    )
                )
            ]

    else
        Html.text ""


viewLoading : Html msg
viewLoading =
    Html.div
        [ class "loading"
        ]
        [ CircularProgress.indeterminate CircularProgress.config
        ]


viewFinished :
    FinishedData
    -> Html Msg
viewFinished data =
    Result.unpack
        (\error ->
            viewCrashed
                { defaultLocale = data.defaultLocale
                , locale = data.locale
                , error = MarkdownError error
                }
        )
        (Html.div [])
        (Markdown.view (Translated.extractDef data "" data.confirmationText))


viewClosed :
    ClosedData
    -> Html Msg
viewClosed data =
    Result.unpack
        (\error ->
            viewCrashed
                { defaultLocale = data.defaultLocale
                , locale = data.locale
                , error = MarkdownError error
                }
        )
        (Html.div [])
        (Markdown.view (Translated.extractDef data "" data.closedText))


viewPaying : PayingData -> Html msg
viewPaying data =
    Html.div [ class "error-view" ]
        [ Result.unpack
            (\error -> Html.pre [] [ Html.text error ])
            (Html.div [])
            (Markdown.view
                (Translated.extractDef data
                    ""
                    (Translated.fromLocale
                        (\locale ->
                            case locale of
                                Locale.De ->
                                    """
# Zahlung

Bitte schließen Sie die Zahlung bei unserem externen Zahlungsprovider ab.
"""

                                Locale.En ->
                                    """
# Checkout

Please complete the payment with our external payment provider.
"""

                                Locale.Es ->
                                    """
# Pago

Por favor, completa el pago con nuestro proveedor de pago externo.
"""

                                Locale.Fr ->
                                    """
# Règlement

Veuillez finaliser le règlement auprès de notre prestataire de paiement externe.
"""

                                Locale.Ru ->
                                    """
# Чек-аут

Пожалуйста, завершите платеж с помощью нашего провайдера платёжных услуг
"""
                        )
                    )
                )
            )
        , Checkout.checkout data.checkout
        ]
